import React from 'react';
import { Calendar, Lock, TrendingDown, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

/**
 * Shows guests simple refund scenarios based on cancellation date
 * Replaces complex formula explanations with easy-to-understand outcomes
 * IMPORTANT: All scenarios show SAME refund amount (locked at booking time)
 */
export function RefundScenarios({ booking, showCancelButton = false, onCancelClick }) {
  const { final_price, locked_refund_pct, check_in, pricing_tier } = booking;
  
  // Generate refund scenarios (all show same locked refund amount)
  const scenarios = generateRefundScenarios(check_in, final_price, locked_refund_pct || 50);
  
  return (
    <div className="space-y-4" data-testid="refund-scenarios">
      <div className="flex items-center justify-between">
        <h4 className="font-bold text-base flex items-center gap-2 text-slate-800">
          <Calendar className="w-5 h-5 text-indigo-600" />
          Your Cancellation Refund Breakdown
        </h4>
        {pricing_tier && (
          <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-300">
            {pricing_tier} Tier
          </Badge>
        )}
      </div>
      
      <div className="space-y-2">
        {scenarios.map((scenario, idx) => (
          <Card
            key={idx}
            className={`p-4 flex justify-between items-center hover:shadow-md transition-all border-l-4 ${
              scenario.isPast 
                ? 'bg-gray-50 border-gray-300 opacity-60' 
                : scenario.isOptimal 
                  ? 'bg-emerald-50 border-emerald-500' 
                  : 'bg-white border-indigo-200'
            }`}
            data-testid={`refund-scenario-${idx}`}
          >
            <div className="flex-1">
              <p className="font-semibold text-sm text-slate-800">
                {scenario.label}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {scenario.dateLabel} {scenario.isPast && '(Past)'}
              </p>
            </div>
            <div className="text-right">
              <p className={`font-bold text-2xl ${
                parseFloat(scenario.refund_amount) > 0 
                  ? 'text-emerald-600' 
                  : 'text-slate-400'
              }`}>
                ${scenario.refund_amount}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {scenario.refund_pct}% refund
              </p>
            </div>
          </Card>
        ))}
      </div>
      
      <Alert className="bg-amber-50 border-amber-300">
        <Lock className="w-4 h-4 text-amber-600" />
        <AlertDescription className="text-amber-800 text-sm">
          <strong>Policy Locked:</strong> These refund amounts were locked when you booked. 
          They will not change regardless of when you cancel.
        </AlertDescription>
      </Alert>
      
      {showCancelButton && onCancelClick && (
        <div className="pt-2">
          <button
            onClick={onCancelClick}
            className="w-full py-3 px-4 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            data-testid="request-cancellation-button"
          >
            <TrendingDown className="w-4 h-4" />
            Request Cancellation ({locked_refund_pct}% refund = ${(final_price * (locked_refund_pct / 100)).toFixed(2)})
          </button>
        </div>
      )}
      
      <div className="text-xs text-center text-muted-foreground">
        💰 Total Paid: <span className="font-bold">${final_price}</span> | 
        🔒 Your refund: <span className="font-bold text-emerald-600">{locked_refund_pct}%</span>
      </div>
    </div>
  );
}

/**
 * Generate refund scenarios showing exact $ amounts for different cancellation dates
 * IMPORTANT: All scenarios show SAME refund amount (locked at booking time)
 * This demonstrates that refund % is locked when booking is created
 */
function generateRefundScenariosWithReliability(checkInDate, totalPrice, lockedRefundPct) {
  if (!checkInDate || !totalPrice) {
    return [];
  }
  
  try {
    const checkIn = new Date(checkInDate);
    const today = new Date();
    const daysUntil = Math.ceil((checkIn - today) / (1000 * 60 * 60 * 24));
    
    // Define scenario breakpoints (days before check-in)
    const breakpoints = [30, 14, 7, 3, 1, 0];
    
    const scenarios = breakpoints.map(daysBefore => {
      const cancelDate = new Date(checkIn);
      cancelDate.setDate(cancelDate.getDate() - daysBefore);
      
      const isPast = cancelDate < today;
      const isToday = daysBefore === daysUntil;
      const isOptimal = daysBefore >= 14 && !isPast; // Best window to cancel
      
      // CRITICAL: Use LOCKED percentage for ALL dates
      // This shows guest that timing doesn't matter - refund is locked
      const refundAmount = (totalPrice * (lockedRefundPct / 100)).toFixed(2);
      
      // Generate label
      let label = '';
      if (daysBefore === 0) label = 'Cancel on check-in day';
      else if (daysBefore === 1) label = 'Cancel 1 day before';
      else if (daysBefore === 3) label = 'Cancel 3 days before';
      else if (daysBefore === 7) label = 'Cancel 1 week before';
      else if (daysBefore === 14) label = 'Cancel 2 weeks before';
      else if (daysBefore === 30) label = 'Cancel 30+ days before';
      
      const dateLabel = cancelDate.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: checkIn.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      });
      
      return {
        label,
        dateLabel,
        refund_amount: refundAmount,
        refund_pct: lockedRefundPct,
        isPast,
        isToday,
        isOptimal
      };
    });
    
    // Filter out past dates except today
    return scenarios.filter(s => !s.isPast || s.isToday);
    
  } catch (error) {
    console.error('Error generating refund scenarios:', error);
    return [];
  }
}

export default RefundScenarios;
